import { useCallback } from 'react';
import { useDuplicateTasksMutation } from 'shared/hooks/queries/use-duplicate-tasks-mutation';
import { useUpdateTaskMutation } from 'shared/hooks/queries/use-update-task-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import { getTaskDocs } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { chunkArray } from 'shared/utils/chunk-array';

import { useTaskById } from '../use-task-by-id';
import { updateLinkedGoal } from './update-linked-goal';
import { updateLinkedLifeArea } from './update-linked-life-area';
import { updateLinkedRelationships } from './update-linked-relationships';

export const useDuplicateTasks = (parentTaskId?: ID) => {
  const { mutateAsync: duplicateTasks } = useDuplicateTasksMutation();
  const { mutateAsync: updateTask } = useUpdateTaskMutation();
  const parentTask = useTaskById(parentTaskId);

  const relationships = useRelationships();

  return useCallback(
    async (tasks: Task[]) => {
      if (!tasks.length) {
        return;
      }

      // Collect all subtasks from all tasks
      const allSubtaskIds = new Set<ID>();
      tasks.forEach((task) => {
        if (task.childIds?.length) {
          task.childIds.forEach((childId) => allSubtaskIds.add(childId));
        }
      });

      // Fetch all subtasks
      const subtasks = allSubtaskIds.size
        ? (
            await Promise.all(
              chunkArray(Array.from(allSubtaskIds), 30).map(
                async (chunkedSubTaskIds) =>
                  await getTaskDocs({ ids: chunkedSubTaskIds }),
              ),
            )
          ).flat(1)
        : [];

      // if a parentTask is provided, ensure all tasks have the parentTask as their parent
      const parentTaskParentIds = parentTask
        ? [...(parentTask.parentIds ?? []), parentTask.id]
        : undefined;
      const preparedTasks = parentTaskParentIds
        ? [...tasks, ...subtasks].map((task) => ({
            ...task,
            parentIds: parentTaskParentIds,
          }))
        : [...tasks, ...subtasks];

      const newTasks = await duplicateTasks(preparedTasks);

      // Create map from old task ID to new task
      const oldToNewTaskMap = new Map<ID, Task>();
      [...tasks, ...subtasks].forEach((oldTask, index) => {
        const newTask = newTasks[index];
        if (newTask) {
          oldToNewTaskMap.set(oldTask.id, newTask);
        }
      });

      // Filter to only main tasks (no parentIds) for entity updates
      const mainTaskPairs = tasks
        .filter((task) => !task.parentIds?.length)
        .map((oldTask) => {
          const newTask = oldToNewTaskMap.get(oldTask.id);
          if (!newTask) {
            return null;
          }
          return { oldTask, newTask };
        })
        .filter(
          (pair): pair is { oldTask: Task; newTask: Task } => pair !== null,
        );

      const updatesPromise: Promise<any>[] = [];

      if (mainTaskPairs.length) {
        updatesPromise.push(
          updateLinkedGoal(mainTaskPairs),
          updateLinkedLifeArea(mainTaskPairs),
          updateLinkedRelationships(
            mainTaskPairs,
            relationships,
          ) as unknown as Promise<void>,
        );
      }

      const updateParentTask = async () => {
        // no parent means no update needed
        if (!parentTask) {
          return;
        }

        // Identify which new tasks we are attaching to the parent.
        // These are the counterparts of the `tasks` passed in.
        const tasksToAttach = tasks
          .map((t) => oldToNewTaskMap.get(t.id))
          .filter((t): t is Task => !!t);

        const newChildIds = tasksToAttach.map((t) => t.id);
        const tasksToAttachIds = new Set(newChildIds);

        let newSubtaskOrder = Array.from(
          new Set([
            ...(parentTask.subtaskOrder ?? []),
            ...(parentTask.childIds ?? []),
          ]),
        );

        // We need to insert new tasks below the task it was duplicated from.
        // Strategy:
        // Iterate over the original order. If we find an ID that was just duplicated,
        // insert the new duplicate's ID right after it.
        const processedNewIds = new Set<ID>();

        if (newSubtaskOrder.length) {
          const nextOrder: ID[] = [];
          for (const existingId of newSubtaskOrder) {
            nextOrder.push(existingId);

            // existingId is the "old" ID. Check if we have a new ID for it.
            const newTask = oldToNewTaskMap.get(existingId);

            // Only insert if this new task is one of the ones we are explicitly attaching
            // (avoid inserting sub-sub-tasks if they appear in the map but weren't roots)
            if (newTask && tasksToAttachIds.has(newTask.id)) {
              nextOrder.push(newTask.id);
              processedNewIds.add(newTask.id);
            }
          }
          newSubtaskOrder = nextOrder;
        }

        // Append any remaining new tasks (whose originals weren't in the order) to the end
        tasksToAttach.forEach((t) => {
          if (!processedNewIds.has(t.id)) {
            newSubtaskOrder.push(t.id);
          }
        });

        await updateTask({
          id: parentTask.id,
          childIds: [...(parentTask.childIds || []), ...newChildIds],
          subtaskOrder: newSubtaskOrder,
        });
      };

      updatesPromise.push(updateParentTask());

      // Update linked entities (each entity updated only once)
      await Promise.all(updatesPromise);
    },
    [duplicateTasks, parentTask, relationships, updateTask],
  );
};
